import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { IState } from '..';
import { getDefaultDateStringValue } from '../../../../../models/UiUtils';
import CalendarIconUrl from '../../../../assets/icon_calendar.svg';
import { DayPicker } from '../../../../components/DayPicker';
import { ToggleAnimationWrapper } from '../../../../components/helpers/ToggleAnimationWrapper';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components-v2/Popover';
import { TextInput } from '../../../../components/TextInput';
import { Toggle } from '../../../../components/Toggle';
import { grayIconFill } from '../../../../styles/colors';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { TagOnCompletion } from '../../shared/components/TagOnCompletion';
import { styleSheet } from '../styles';
import { EventRegistrationFormData } from '../utils';

interface AttendeesSettingsProps {
	state: {
		enableRequestAttendeesToRSVP: boolean;
		now: Date;
		selectedImage: Api.IFileAttachment | null;
	};
	eventData?: Api.IEventRegistrationSurvey;
	account?: Api.IAccount;
	publishableApiKeys?: Api.IStripeKeys;
	onToggleCheckChanged: () => void;
	onClickMedia: () => void;
	onClickFreeImage: () => void;
	isClone?: boolean;
	setState: (state: React.Dispatch<React.SetStateAction<IState>>) => void;
}

export const AttendeesSettings: React.FC<AttendeesSettingsProps> = () => {
	const { control, watch, setValue, formState } = useFormContext<EventRegistrationFormData>();
	const formValues = watch();

	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>Attendees</h3>
			<div>
				<TagOnCompletion />
			</div>

			<FormFieldWrapper
				name='enableRequestAttendeesToRSVP'
				control={control}
				title='Enable Attendees RSVP Options'
				description='Allow attendees to RSVP with additional options'
			>
				{({ value, onChange }) => (
					<Toggle
						id='request-attendees-rsvp-toggle'
						isOn={value}
						onToggleCheckChanged={() => onChange(!value)}
						uncheckedColor={grayIconFill}
					/>
				)}
			</FormFieldWrapper>

			<ToggleAnimationWrapper isVisible={formValues.enableRequestAttendeesToRSVP}>
				<fieldset disabled={!formValues.enableRequestAttendeesToRSVP} className={css(styleSheet.fieldset)}>
					<FormFieldWrapper
						name='requirePhoneNumber'
						control={control}
						title='Require Phone Number'
						description='Require attendees to provide phone number'
					>
						{({ value, onChange }) => (
							<Toggle
								id='require-attendees-phone-number-checkbox'
								isOn={value}
								onToggleCheckChanged={() => {
									if (!formValues.enableRequestAttendeesToRSVP) {
										return;
									}
									onChange(!value);
								}}
								uncheckedColor={grayIconFill}
							/>
						)}
					</FormFieldWrapper>
					<FormFieldWrapper
						name='requireGuestNames'
						control={control}
						title='Require Guest Names'
						description='Require guests to provide their names (disables anonymous guest count)'
					>
						{({ value, onChange }) => (
							<Toggle
								id='require-guest-names-checkbox'
								isOn={value}
								onToggleCheckChanged={() => {
									if (!formValues.enableRequestAttendeesToRSVP) {
										return;
									}
									onChange(!value);
								}}
								uncheckedColor={grayIconFill}
							/>
						)}
					</FormFieldWrapper>
					<FormFieldWrapper
						name='requireDeadline'
						control={control}
						title='RSVP Deadline'
						description='Please RSVP by… (deadline)'
					>
						{({ value, onChange }) => (
							<Toggle
								id='rsvp-by-deadline-checkbox'
								isOn={value}
								onToggleCheckChanged={() => {
									if (!formValues.enableRequestAttendeesToRSVP) {
										return;
									}
									if (!value) {
										setValue('registrationDeadline', null);
									}
									onChange(!value);
								}}
								uncheckedColor={grayIconFill}
							/>
						)}
					</FormFieldWrapper>
					<div className={css(styleSheet.formControlWrap)}>
						<ToggleAnimationWrapper isVisible={formValues.requireDeadline}>
							<div className={css(styleSheet.deadlineDateInput)}>
								<Controller
									name='registrationDeadline'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => {
										return (
											<>
												<Popover>
													<PopoverTrigger asChild>
														<span className={css(styleSheet.eventDateInput, styleSheet.eventDateInputSmall)}>
															<img src={CalendarIconUrl} className={css(styleSheet.calendarIcon)} alt='Calendar icon' />
															<span className={css(styleSheet.selectedDate)}>
																{fieldProps.value && getDefaultDateStringValue(fieldProps.value)}
															</span>
														</span>
													</PopoverTrigger>
													<PopoverContent>
														<div style={{ padding: '20px 10px' }}>
															<DayPicker
																allowPastDates={false}
																minDate={new Date()}
																maxDate={new Date(formValues.endDate)}
																onDayClick={(val: Date) => {
																	fieldProps.onChange(val);
																}}
																selectedDays={
																	fieldProps.value
																		? new Date(fieldProps.value)
																		: formValues.startDate
																			? new Date(formValues.startDate)
																			: new Date()
																}
															/>
														</div>
													</PopoverContent>
												</Popover>

												{fieldProps.value && (
													<button
														type='button'
														className={css(styleSheet.clearDeadline)}
														onClick={(e: React.MouseEvent) => {
															e.preventDefault();
															fieldProps.onChange(null);
														}}
													>
														Clear
													</button>
												)}
											</>
										);
									}}
								/>
								{formState.isSubmitted && formState.errors.registrationDeadline && (
									<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>
										{formState.errors.registrationDeadline.message}
									</p>
								)}
							</div>
						</ToggleAnimationWrapper>
					</div>

					{/* Guest Limit and Maximum Capacity - only show when RSVP is enabled and event is not paid */}
					{!formValues.isPaidEvent && (
						<>
							<FormFieldWrapper
								name='setGuestLimit'
								control={control}
								title='Set Guest Limit'
								description='Set maximum guests allowed'
							>
								{({ value, onChange }) => (
									<Toggle
										id='set-guest-limit-checkbox'
										isOn={value}
										onToggleCheckChanged={() => {
											if (!formValues.enableRequestAttendeesToRSVP) {
												return;
											}
											const toggle = !value;
											if (toggle !== true) {
												setValue('guestLimit', formState.defaultValues?.guestLimit);
											}
											onChange(toggle);
										}}
										uncheckedColor={grayIconFill}
									/>
								)}
							</FormFieldWrapper>
							<div className={css(styleSheet.formControlWrap)}>
								<ToggleAnimationWrapper isVisible={formValues.setGuestLimit}>
									<div className={css(styleSheet.maximumCapasityInput)}>
										<Controller
											name='guestLimit'
											control={control}
											render={({ field: { ref, ...fieldProps } }) => (
												<TextInput
													id='guestLimit'
													inputId='guest-limit-input'
													inputRef={ref}
													min={1}
													type='number'
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
														fieldProps.onChange(e.target.value);
													}}
													value={fieldProps.value}
												/>
											)}
										/>
										{formState.isSubmitted && formState.errors.guestLimit && (
											<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>
												{formState.errors.guestLimit.message}
											</p>
										)}
									</div>
								</ToggleAnimationWrapper>
							</div>

							<FormFieldWrapper
								name='requireMaximumCapacity'
								control={control}
								title='Set Maximum Capacity'
								description='Set maximum capacity'
							>
								{({ value, onChange }) => (
									<Toggle
										id='set-maximum-capacity-checkbox'
										isOn={value}
										onToggleCheckChanged={() => {
											if (!formValues.enableRequestAttendeesToRSVP) {
												return;
											}
											const toggle = !value;
											if (toggle !== true) {
												setValue('maximumCapacity', formState.defaultValues?.maximumCapacity);
											}
											onChange(toggle);
										}}
										uncheckedColor={grayIconFill}
									/>
								)}
							</FormFieldWrapper>
							<div className={css(styleSheet.formControlWrap)}>
								<ToggleAnimationWrapper isVisible={formValues.requireMaximumCapacity}>
									<div className={css(styleSheet.maximumCapasityInput)}>
										<Controller
											name='maximumCapacity'
											control={control}
											render={({ field: { ref, ...fieldProps } }) => (
												<TextInput
													id='maximumCapacity'
													inputId='maximum-capacity-input'
													inputRef={ref}
													min={1}
													type='number'
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
														fieldProps.onChange(e.target.value);
													}}
													value={fieldProps.value}
												/>
											)}
										/>
										{formState.isSubmitted && formState.errors.maximumCapacity && (
											<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>
												{formState.errors.maximumCapacity.message}
											</p>
										)}
									</div>
								</ToggleAnimationWrapper>
							</div>
						</>
					)}
				</fieldset>
			</ToggleAnimationWrapper>
		</section>
	);
};
